import React, { useEffect } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTheme } from 'providers/Theme.provider';

import BookingDescriptionComponent from 'clientProfile/components/bookingHistory/BookingDescription';

import CN from './BookingHistoryContainer.module.scss';

const BE_PAGE_SIZE_LIMIT = 10;

const BookingHistoryContainer = (props) => {
  const { isDarkMode } = useTheme();

  const { data, getMoreBookingHistory, isLoading, showMoreButtonRef } = props;

  const { val: isFoldOpen, tip: toggleFold } = useTip(false);

  useEffect(() => {
    if (isLoading && showMoreButtonRef.current) {
      showMoreButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isLoading, showMoreButtonRef]);

  const filteredData = [];
  if (data) {
    const voidedDataArr = data.filter((d) => d.transaction && d.transaction.is_void);
    const keys = data.map((e) => e.id);
    const uniqueKeys = Array.from(new Set(keys));
    if (voidedDataArr.length > 0) {
      for (let i = 0; i < voidedDataArr.length; i += 1) {
        for (let j = 0; j < data.length; j += 1) {
          if (
            data[j].id !== voidedDataArr[i].id ||
            (data[j].id === voidedDataArr[i].id && data[j].transaction && data[j].transaction.is_void)
          ) {
            if (!filteredData.find((e) => e.id === data[j].id)) {
              filteredData.push(data[j]);
            }
            if (data[j].transaction && data[j].transaction.is_void) {
              data[j].status = 'Transaction voided';
            }
          }
        }
      }
    } else {
      for (let i = 0; i < uniqueKeys.length; i += 1) {
        filteredData.push(data.find((e) => e.id === uniqueKeys[i]));
      }
    }
  }

  return (
    <>
      <div
        data-bem="BookingHistoryContainer__fold"
        className={cn({
          bh: true,
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <header data-bem="BookingHistoryContainer__header" className="bgr" />

        <div className={CN.flex}>
          <h3 data-bem="BookingHistoryContainer__title">Booking History </h3>
          <Button tag="BookingHistoryContainer__button" className={CN.button} onClick={toggleFold} variant="secondary">
            <div className={CN.toggleButtonContainer}>
              <span className={CN.underline}>What's included</span>
              <Icon
                tag="BookingHistoryContainer__arrow"
                variant="arrowFoldoutPurple"
                selected={!isFoldOpen}
                unselected={isFoldOpen}
                width="lg"
              />
            </div>
          </Button>
        </div>

        {isFoldOpen && (
          <div data-bem="BookingHistoryContainer__fold" className={CN.historyFold}>
            <span className={CN.dot}>
              All past bookings (inc waitlist, deleted, deleted stylist, no-show and cancelled) with the exception of
              deleted repeat bookings which won{S.apos}t be shown
            </span>
            <span className={CN.dot}>
              Click {S.quoteOpen}view details{S.quoteClose} to see:
            </span>
            <ul className={CN.list}>
              <li className={CN.item}>- Payment info for checked out bookings</li>
              <li className={CN.item}>- Option to re-send receipt for checked out bookings</li>
              <li className={CN.item}>- SMS sent and client replies</li>
              <li className={CN.item}>- Booking notes</li>
            </ul>
            <span className={CN.dot}>
              No-show charges are no longer displayed in client profile; details can be found in Cashup
            </span>
          </div>
        )}

        {filteredData &&
          filteredData.map((item) => (
            <BookingDescriptionComponent
              key={`${item.id}_${item.timestamp}_${Date.now()}`}
              appointment={item}
              goToViewDetails={props.goToViewDetails}
              onAppointmentClick={props.onAppointmentClick}
              detailsView={false}
              footer
            />
          ))}
      </div>
      {props.remainingHistoryBookings > BE_PAGE_SIZE_LIMIT && (
        <div data-bem="BookingHistoryContainer__show-more-container" className={CN.showMoreContainer}>
          <Button
            ref={showMoreButtonRef}
            variant="primaryForce"
            tag="BookingHistoryContainer__show-more-button"
            onClick={getMoreBookingHistory}
          >
            <span className={CN.buttonSpace}>SHOW MORE</span>
          </Button>
        </div>
      )}
      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default BookingHistoryContainer;
